import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Target, Plus, Trash2, Check, TrendingDown, TrendingUp, Dumbbell, Scale, Percent, Calendar, Loader2, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import Paywall from "@/components/Paywall";
import { toast } from "sonner";

const GOAL_TYPES = {
  weight_loss: { name: "Tab vægt", icon: TrendingDown, unit: "kg", color: "text-blue-500" },
  weight_gain: { name: "Tag på (muskelmasse)", icon: TrendingUp, unit: "kg", color: "text-green-500" },
  body_fat: { name: "Reducer fedtprocent", icon: Percent, unit: "%", color: "text-orange-500" },
  workouts_per_week: { name: "Træninger pr. uge", icon: Dumbbell, unit: "træninger", color: "text-purple-500" },
  custom: { name: "Brugerdefineret mål", icon: Target, unit: "", color: "text-[#DFFF00]" },
};

export default function GoalsPage() {
  const { authHeaders } = useAuth();
  const [goals, setGoals] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newGoal, setNewGoal] = useState({
    goal_type: "weight_loss",
    target_value: "",
    target_date: "",
    description: ""
  });

  useEffect(() => {
    fetchGoals();
    fetchSuggestions();
  }, []);

  const fetchGoals = async () => {
    try {
      const res = await axios.get(`${API}/goals`, { headers: authHeaders() });
      setGoals(res.data);
    } catch (err) {
      console.error("Error fetching goals:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await axios.get(`${API}/goals/suggestions`, { headers: authHeaders() });
      setSuggestions(res.data);
    } catch (err) {
      console.error("Error fetching suggestions:", err);
    }
  };

  const handleCreateGoal = async () => {
    if (!newGoal.target_value) {
      toast.error("Indtast en målværdi");
      return;
    }
    
    setCreating(true);
    try {
      const goalData = {
        goal_type: newGoal.goal_type,
        target_value: parseFloat(newGoal.target_value),
        target_date: newGoal.target_date || null,
        description: newGoal.description || GOAL_TYPES[newGoal.goal_type]?.name || ""
      };
      
      await axios.post(`${API}/goals`, goalData, { headers: authHeaders() });
      toast.success("Mål oprettet! 🎯");
      setCreateOpen(false);
      setNewGoal({ goal_type: "weight_loss", target_value: "", target_date: "", description: "" });
      fetchGoals();
    } catch (err) {
      toast.error("Kunne ikke oprette mål");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGoal = async (goalId) => {
    if (!confirm("Er du sikker på du vil slette dette mål?")) return;
    
    try {
      await axios.delete(`${API}/goals/${goalId}`, { headers: authHeaders() });
      toast.success("Mål slettet");
      setGoals(goals.filter(g => g.id !== goalId));
    } catch (err) {
      toast.error("Kunne ikke slette mål");
    }
  };

  const handleCompleteGoal = async (goalId) => {
    try {
      await axios.put(`${API}/goals/${goalId}`, { completed: true }, { headers: authHeaders() });
      toast.success("Tillykke! Mål fuldført! 🎉");
      fetchGoals();
    } catch (err) {
      toast.error("Kunne ikke markere mål som fuldført");
    }
  };

  const calculateProgress = (goal) => {
    if (!goal.start_value && goal.goal_type !== "workouts_per_week") return 0;
    
    const current = goal.current_value || 0;
    const target = goal.target_value;
    const start = goal.start_value || 0;
    
    if (goal.goal_type === "weight_loss" || goal.goal_type === "body_fat") {
      // Progress towards decrease
      const totalChange = start - target;
      const actualChange = start - current;
      if (totalChange === 0) return current <= target ? 100 : 0;
      return Math.min(100, Math.max(0, (actualChange / totalChange) * 100));
    } else if (goal.goal_type === "workouts_per_week") {
      // Simple progress towards target
      return Math.min(100, (current / target) * 100);
    } else {
      // Progress towards increase
      const totalChange = target - start;
      const actualChange = current - start;
      if (totalChange === 0) return current >= target ? 100 : 0;
      return Math.min(100, Math.max(0, (actualChange / totalChange) * 100));
    }
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  return (
    <Paywall>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in-up" data-testid="goals-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-1">
              Mål & Milestones
            </h1>
            <p className="text-zinc-400">Sæt mål og track din fremgang</p>
          </div>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#DFFF00] text-black hover:bg-[#C8E600] rounded-lg font-bold" data-testid="create-goal-btn">
                <Plus className="h-4 w-4 mr-2" /> Nyt Mål
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#18181b] border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Oswald, sans-serif' }} className="text-xl uppercase">
                  Opret Nyt Mål
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Måltype</Label>
                  <Select value={newGoal.goal_type} onValueChange={(v) => setNewGoal({...newGoal, goal_type: v})}>
                    <SelectTrigger className="bg-zinc-900 border-zinc-700 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      {Object.entries(GOAL_TYPES).map(([key, info]) => (
                        <SelectItem key={key} value={key} className="text-white hover:bg-zinc-800">
                          <div className="flex items-center gap-2">
                            <info.icon className={`h-4 w-4 ${info.color}`} />
                            {info.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">
                    Målværdi ({GOAL_TYPES[newGoal.goal_type]?.unit || ""})
                  </Label>
                  <Input
                    type="number"
                    value={newGoal.target_value}
                    onChange={(e) => setNewGoal({...newGoal, target_value: e.target.value})}
                    className="bg-zinc-900 border-zinc-700 mt-1"
                    placeholder={newGoal.goal_type === "weight_loss" ? "75" : newGoal.goal_type === "workouts_per_week" ? "4" : ""}
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Deadline (valgfrit)</Label>
                  <Input
                    type="date"
                    value={newGoal.target_date}
                    onChange={(e) => setNewGoal({...newGoal, target_date: e.target.value})}
                    className="bg-zinc-900 border-zinc-700 mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Beskrivelse (valgfrit)</Label>
                  <Input
                    value={newGoal.description}
                    onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                    className="bg-zinc-900 border-zinc-700 mt-1"
                    placeholder="Mit personlige mål..."
                  />
                </div>

                <Button 
                  onClick={handleCreateGoal} 
                  disabled={creating}
                  className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] font-bold mt-4"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Target className="h-4 w-4 mr-2" />}
                  Opret Mål
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Suggestions */}
        {suggestions.length > 0 && activeGoals.length === 0 && (
          <Card className="bg-gradient-to-br from-[#DFFF00]/10 to-transparent border-[#DFFF00]/20 mb-8">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-[#DFFF00]">
                <Sparkles className="h-5 w-5" /> Foreslåede Mål
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {suggestions.slice(0, 4).map((suggestion, i) => {
                  const info = GOAL_TYPES[suggestion.goal_type];
                  const Icon = info?.icon || Target;
                  
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        setNewGoal({
                          goal_type: suggestion.goal_type,
                          target_value: Math.abs(suggestion.suggested_target).toString(),
                          target_date: "",
                          description: suggestion.description
                        });
                        setCreateOpen(true);
                      }}
                      className="flex items-center gap-3 p-3 bg-zinc-800/50 hover:bg-zinc-800 rounded-lg transition-colors text-left"
                    >
                      <Icon className={`h-5 w-5 ${info?.color}`} />
                      <div>
                        <p className="font-medium text-white">{suggestion.description}</p>
                        <p className="text-xs text-zinc-500">{suggestion.timeframe}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Goals */}
        <div className="mb-8">
          <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-[#DFFF00]" />
            Aktive Mål ({activeGoals.length})
          </h2>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2].map(i => (
                <Card key={i} className="bg-[#18181b] border-zinc-800/50 animate-pulse">
                  <CardContent className="p-6 h-32" />
                </Card>
              ))}
            </div>
          ) : activeGoals.length === 0 ? (
            <Card className="bg-[#18181b] border-zinc-800/50">
              <CardContent className="p-8 text-center">
                <Target className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500 mb-4">Du har ingen aktive mål endnu</p>
                <Button onClick={() => setCreateOpen(true)} variant="outline" className="border-zinc-700">
                  <Plus className="h-4 w-4 mr-2" /> Opret dit første mål
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {activeGoals.map(goal => {
                const info = GOAL_TYPES[goal.goal_type];
                const Icon = info?.icon || Target;
                const progress = calculateProgress(goal);
                
                return (
                  <Card key={goal.id} className="bg-[#18181b] border-zinc-800/50" data-testid={`goal-${goal.id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center`}>
                            <Icon className={`h-6 w-6 ${info?.color}`} />
                          </div>
                          <div>
                            <h3 className="font-bold text-white">{goal.description || info?.name}</h3>
                            <p className="text-sm text-zinc-500">
                              Mål: {goal.target_value} {info?.unit}
                              {goal.target_date && ` • Deadline: ${new Date(goal.target_date).toLocaleDateString('da-DK')}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            onClick={() => handleCompleteGoal(goal.id)}
                            size="sm"
                            className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button 
                            onClick={() => handleDeleteGoal(goal.id)}
                            size="sm"
                            variant="ghost"
                            className="text-zinc-500 hover:text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-zinc-400">
                            Nuværende: <span className="text-white font-bold">{goal.current_value || 0} {info?.unit}</span>
                          </span>
                          <span className="text-[#DFFF00] font-bold">{Math.round(progress)}%</span>
                        </div>
                        <Progress value={progress} className="h-3 bg-zinc-800" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Completed Goals */}
        {completedGoals.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Fuldførte Mål ({completedGoals.length})
            </h2>
            
            <div className="space-y-3">
              {completedGoals.map(goal => {
                const info = GOAL_TYPES[goal.goal_type];
                const Icon = info?.icon || Target;
                
                return (
                  <Card key={goal.id} className="bg-[#18181b]/50 border-zinc-800/30">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                            <Check className="h-5 w-5 text-green-500" />
                          </div>
                          <div>
                            <h3 className="font-medium text-white">{goal.description || info?.name}</h3>
                            <p className="text-xs text-zinc-500">
                              Fuldført: {goal.completed_at ? new Date(goal.completed_at).toLocaleDateString('da-DK') : 'Ukendt'}
                            </p>
                          </div>
                        </div>
                        <Button 
                          onClick={() => handleDeleteGoal(goal.id)}
                          size="sm"
                          variant="ghost"
                          className="text-zinc-600 hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Paywall>
  );
}
